import { useCallback, useEffect, useRef, useState } from "react";
import type { All8PageContent } from "@bcc/core-renderers";

export type ComponentHistoryEntry = {
	/** Wall-clock timestamp (ms) when this state was recorded. */
	at: number;
	/** Number of top-level components in the snapshot (a light label for the panel). */
	count: number;
};

export type ComponentHistory = {
	canUndo: boolean;
	canRedo: boolean;
	undo: () => void;
	redo: () => void;
	/** Chronological timeline of recorded states (index 0 = oldest). */
	entries: ComponentHistoryEntry[];
	/** Index of the entry currently reflected in the editor. */
	currentIndex: number;
	/** Jump straight to a recorded state (used by the history modal). */
	jumpTo: (index: number) => void;
};

type UseComponentHistoryOptions = {
	/** The current component list (the « present » state). */
	value: All8PageContent;
	/** Re-applies a snapshot to the editing surface (undo / redo / jump target). */
	apply: (next: All8PageContent) => void;
	/** Idle delay (ms) before a burst of edits is committed as one timeline entry. */
	delay?: number;
	/** When this changes the whole timeline is reset (e.g. a new document loads). */
	resetKey?: string;
};

/** Hard cap on the timeline length; oldest entries are dropped past this. */
const MAX_ENTRIES = 100;

type Entry = { snapshot: All8PageContent; at: number };

function clone(value: All8PageContent): All8PageContent {
	return JSON.parse(JSON.stringify(value ?? [])) as All8PageContent;
}

function serialize(value: All8PageContent): string {
	return JSON.stringify(value ?? []);
}

/**
 * Session-scoped, NON-DESTRUCTIVE history for a component list. Every settled
 * edit is APPENDED to a chronological timeline and becomes the current entry : * an edit never truncates the « redo » side, even when the cursor was in the
 * middle of the timeline. Undo / redo move the cursor without mutating the list,
 * and any entry can be jumped to directly (with its timestamp) from the history
 * modal.
 *
 * Snapshots are DEEP COPIES, coalesced: a burst of rapid edits (typing) collapses
 * into one entry once editing has been idle for `delay` ms, so not every keystroke
 * becomes a timeline point.
 *
 * The hook is surface-agnostic: it observes `value` and calls `apply` to revert.
 * Both the full page editor (`draft.components`) and the embedded content-body
 * editor (`componentsState`) drive the same implementation.
 */
export function useComponentHistory({
	value,
	apply,
	delay = 500,
	resetKey
}: UseComponentHistoryOptions): ComponentHistory {
	const entriesRef = useRef<Entry[] | null>(null);
	if (entriesRef.current === null) {
		entriesRef.current = [{ snapshot: clone(value), at: Date.now() }];
	}
	const cursorRef = useRef(0);
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	// True while we re-apply a snapshot, so the resulting `value` change is not
	// recorded as a new user edit.
	const applyingRef = useRef(false);
	// True while a burst of edits is in flight but not yet committed as an entry.
	const pendingRef = useRef(false);
	const prevValueRef = useRef<All8PageContent>(value);
	const applyRef = useRef(apply);
	applyRef.current = apply;
	const delayRef = useRef(delay);
	delayRef.current = delay;

	// Bump to re-render when the timeline / cursor changes (they read from refs).
	const [, forceRender] = useState(0);
	const rerender = useCallback(() => forceRender((n) => n + 1), []);

	const clearTimer = useCallback(() => {
		if (timerRef.current) {
			clearTimeout(timerRef.current);
			timerRef.current = null;
		}
	}, []);

	// Append the in-flight edit as a new timeline entry and make it current.
	const commitPending = useCallback(() => {
		clearTimer();
		if (!pendingRef.current) return;
		const entries = entriesRef.current!;
		entries.push({ snapshot: clone(prevValueRef.current), at: Date.now() });
		if (entries.length > MAX_ENTRIES) {
			entries.splice(0, entries.length - MAX_ENTRIES);
		}
		cursorRef.current = entries.length - 1;
		pendingRef.current = false;
	}, [clearTimer]);

	// Reset the whole timeline when the document identity changes.
	useEffect(() => {
		clearTimer();
		entriesRef.current = [{ snapshot: clone(value), at: Date.now() }];
		cursorRef.current = 0;
		pendingRef.current = false;
		applyingRef.current = false;
		prevValueRef.current = value;
		rerender();
		// Only on resetKey change : `value` is intentionally excluded so a reset
		// re-baselines to the current value without recording an edit.
	}, [resetKey, clearTimer, rerender]);

	// Observe value changes: record user edits (debounced), ignore our own applies.
	useEffect(() => {
		if (applyingRef.current) {
			applyingRef.current = false;
			prevValueRef.current = value;
			return;
		}
		// Same reference ⇒ mount, reset, or an unrelated parent re-render.
		if (value === prevValueRef.current) return;
		// Structurally identical ⇒ a round-trip resync or a no-op commit: adopt the
		// new reference but don't record a duplicate entry.
		if (serialize(value) === serialize(prevValueRef.current)) {
			prevValueRef.current = value;
			return;
		}
		pendingRef.current = true;
		clearTimer();
		timerRef.current = setTimeout(() => {
			commitPending();
			rerender();
		}, delayRef.current);
		prevValueRef.current = value;
		rerender();
	}, [value, clearTimer, commitPending, rerender]);

	// Cleanup on unmount.
	useEffect(() => () => clearTimer(), [clearTimer]);

	const applyIndex = useCallback((index: number) => {
		const entries = entriesRef.current!;
		cursorRef.current = index;
		applyingRef.current = true;
		applyRef.current(clone(entries[index].snapshot));
	}, []);

	const undo = useCallback(() => {
		commitPending();
		if (cursorRef.current <= 0) return;
		applyIndex(cursorRef.current - 1);
		rerender();
	}, [applyIndex, commitPending, rerender]);

	const redo = useCallback(() => {
		commitPending();
		if (cursorRef.current >= entriesRef.current!.length - 1) return;
		applyIndex(cursorRef.current + 1);
		rerender();
	}, [applyIndex, commitPending, rerender]);

	const jumpTo = useCallback(
		(index: number) => {
			commitPending();
			const entries = entriesRef.current!;
			if (index < 0 || index >= entries.length) return;
			applyIndex(index);
			rerender();
		},
		[applyIndex, commitPending, rerender]
	);

	const entries = entriesRef.current!.map((e) => ({
		at: e.at,
		count: Array.isArray(e.snapshot) ? e.snapshot.length : 0
	}));
	const canUndo = pendingRef.current || cursorRef.current > 0;
	const canRedo = !pendingRef.current && cursorRef.current < entriesRef.current!.length - 1;

	return { canUndo, canRedo, undo, redo, entries, currentIndex: cursorRef.current, jumpTo };
}
